// ─── Tax return estimator (planning tool, not tax advice) ────
// Rough federal estimate for tax-year 2025 (returns filed in 2026).
// Texas has no personal income tax; TX entities owe franchise tax only
// above the no-tax-due revenue threshold.

export interface TaxEstimateInputs {
  filingStatus: 'mfj' | 'single'
  wages: number // W-2 box 1, household total
  federalWithholding: number
  seNetIncome: number // combined Schedule C / K-1 SE earnings
  otherIncome: number // interest, dividends, net capital gains
  preTaxContributions: number // solo 401(k), SEP, traditional IRA, HSA
  itemizedDeductions: number // 0 = use standard deduction
  dependentsUnder17: number
  estimatedPaymentsMade: number // 1040-ES paid so far
}

export interface TaxEstimateLine {
  label: string
  amount: number
  note?: string
}

export interface TaxEstimateResult {
  totalIncome: number
  seTax: number
  agi: number
  deduction: number
  deductionType: 'standard' | 'itemized'
  qbiDeduction: number
  taxableIncome: number
  incomeTax: number
  childTaxCredit: number
  totalTax: number
  totalPayments: number
  refundOrOwed: number // positive = refund
  effectiveRate: number
  marginalRate: number
  lines: TaxEstimateLine[]
}

// 2025 federal brackets
const BRACKETS: Record<'mfj' | 'single', Array<[number, number]>> = {
  mfj: [
    [23850, 0.1],
    [96950, 0.12],
    [206700, 0.22],
    [394600, 0.24],
    [501050, 0.32],
    [751600, 0.35],
    [Infinity, 0.37],
  ],
  single: [
    [11925, 0.1],
    [48475, 0.12],
    [103350, 0.22],
    [197300, 0.24],
    [250525, 0.32],
    [626350, 0.35],
    [Infinity, 0.37],
  ],
}

const STANDARD_DEDUCTION = { mfj: 31500, single: 15750 }
const SS_WAGE_BASE = 176100
const CTC_PER_CHILD = 2200

function bracketTax(taxable: number, status: 'mfj' | 'single'): { tax: number; marginal: number } {
  let tax = 0
  let prev = 0
  let marginal = 0.1
  for (const [cap, rate] of BRACKETS[status]) {
    if (taxable <= prev) break
    const inBracket = Math.min(taxable, cap) - prev
    tax += inBracket * rate
    marginal = rate
    prev = cap
  }
  return { tax, marginal }
}

export function estimateFederal(inputs: TaxEstimateInputs): TaxEstimateResult {
  const wages = Math.max(0, inputs.wages)
  const se = Math.max(0, inputs.seNetIncome)
  const other = inputs.otherIncome

  // Self-employment tax: 92.35% of net SE earnings; 12.4% SS up to the wage
  // base (reduced by W-2 wages already taxed), 2.9% Medicare on all of it.
  const seBase = se * 0.9235
  const ssRoom = Math.max(0, SS_WAGE_BASE - wages)
  const ssTax = Math.min(seBase, ssRoom) * 0.124
  const medicareTax = seBase * 0.029
  const seTax = ssTax + medicareTax
  const halfSeDeduction = seTax / 2

  const totalIncome = wages + se + other
  const agi = totalIncome - halfSeDeduction - Math.max(0, inputs.preTaxContributions)

  const standard = STANDARD_DEDUCTION[inputs.filingStatus]
  const useItemized = inputs.itemizedDeductions > standard
  const deduction = useItemized ? inputs.itemizedDeductions : standard

  // Simplified QBI: 20% of qualified SE income, capped at 20% of ordinary
  // taxable income. SSTB phase-outs above the threshold are NOT modeled.
  const taxableBeforeQbi = Math.max(0, agi - deduction)
  const qbiBase = Math.max(0, se - halfSeDeduction)
  const qbiDeduction = Math.min(qbiBase * 0.2, taxableBeforeQbi * 0.2)

  const taxableIncome = Math.max(0, taxableBeforeQbi - qbiDeduction)
  const { tax: incomeTax, marginal } = bracketTax(taxableIncome, inputs.filingStatus)

  const childTaxCredit = Math.max(0, inputs.dependentsUnder17) * CTC_PER_CHILD
  const totalTax = Math.max(0, incomeTax - childTaxCredit) + seTax

  const totalPayments = Math.max(0, inputs.federalWithholding) + Math.max(0, inputs.estimatedPaymentsMade)
  const refundOrOwed = totalPayments - totalTax

  const lines: TaxEstimateLine[] = [
    { label: 'Total income', amount: totalIncome },
    { label: 'Self-employment tax', amount: seTax, note: '15.3% on 92.35% of SE income (SS capped)' },
    { label: '½ SE tax + pre-tax contributions', amount: -(halfSeDeduction + inputs.preTaxContributions) },
    { label: 'Adjusted gross income', amount: agi },
    {
      label: useItemized ? 'Itemized deductions' : 'Standard deduction',
      amount: -deduction,
    },
    { label: 'QBI deduction (simplified)', amount: -qbiDeduction, note: 'SSTB phase-out not modeled' },
    { label: 'Taxable income', amount: taxableIncome },
    { label: 'Income tax', amount: incomeTax },
    { label: 'Child tax credit', amount: -childTaxCredit },
    { label: 'Total tax (incl. SE tax)', amount: totalTax },
    { label: 'Withholding + estimated payments', amount: -totalPayments },
  ]

  return {
    totalIncome,
    seTax,
    agi,
    deduction,
    deductionType: useItemized ? 'itemized' : 'standard',
    qbiDeduction,
    taxableIncome,
    incomeTax,
    childTaxCredit,
    totalTax,
    totalPayments,
    refundOrOwed,
    effectiveRate: totalIncome > 0 ? totalTax / totalIncome : 0,
    marginalRate: marginal,
    lines,
  }
}

// Texas franchise tax: below the no-tax-due revenue threshold ($2.47M for
// 2024–25 reports) an entity owes $0 but must still file a Public
// Information Report.
export const TX_NO_TAX_DUE_THRESHOLD = 2_470_000

export function txFranchiseNote(annualRevenue: number): string {
  return annualRevenue <= TX_NO_TAX_DUE_THRESHOLD
    ? 'Under the no-tax-due threshold — $0 due, Public Information Report still required (May 15)'
    : 'Above the no-tax-due threshold — franchise tax return required (May 15)'
}
